import { Divider, Flex, Text } from '@mantine/core';
import { useSetAtom } from 'jotai/index';
import { useGlobalState } from '../../data/Global.state';
import { usePutApiInventoryListingListingIdReserve, usePutApiInventoryListingListingIdUnreserve } from '../../api';
import { BarkerEventListing } from '../../types';
import { updateListingsAtom } from '../../data/atoms';
import { BNButton } from '../../components/Button/Button';
import { useDTIInventory } from '../DTI/DTI.Inventory.hooks';
import { useMemo } from 'react';
import { isDtiHosted } from '../../utils/whitelabel-consts';

export function ReserveUnreserveAction({ onClose, listing }: { onClose: () => void; listing: BarkerEventListing }) {
  const { currentUser, tenants } = useGlobalState('currentUser', 'tenants');
  const tenantPointOfSale = useMemo(() => tenants?.find((t) => t.tenantId === listing.event?.tenantId)?.pointOfSaleId || 'Unknown', [listing.event?.tenantId, tenants]);
  const updateListing = useSetAtom(updateListingsAtom);
  const { mutateAsync: reserveMutation, isPending: isReserving } = usePutApiInventoryListingListingIdReserve({
    axios: {
      headers: {
        'x-tenant-id': listing.tenantId,
      },
    },
  });

  const { mutateAsync: unreserveMutation, isPending: isUnreserving } = usePutApiInventoryListingListingIdUnreserve({
    axios: {
      headers: {
        'x-tenant-id': listing.tenantId,
      },
    },
  });
  const {
    selectedListing: dtiSelectedListing,
    reserveListing: dtiReserveListing,
    unreserveListing: dtiUnreserveListing,
    isBulkUpdating: dtiIsBulkUpdating,
  } = useDTIInventory('selectedListing', 'reserveListing', 'unreserveListing', 'isBulkUpdating');

  const useDti = isDtiHosted && tenantPointOfSale === 'DtiPortal';
  const isOnHold = ((useDti ? dtiSelectedListing : listing)?.quantityReserved ?? 0) > 0;

  const reserveListing = useDti
    ? dtiReserveListing
    : async ({ listingId }: { listingId: string }) => {
      const response = await reserveMutation({ listingId });
      updateListing(response.data);
    };

  const unreserveListing = useDti
    ? dtiUnreserveListing
    : async ({ listingId }: { listingId: string }) => {
      const response = await unreserveMutation({ listingId });
      updateListing(response.data);
    };

  const isLoading = useDti ? dtiIsBulkUpdating : (isReserving || isUnreserving);
  return (
    <>
      <Flex p={0} h="100%" justify="space-between">
        <Flex py="sm" px="md" gap="xs" align="center" justify="start" style={{ flex: 1 }}>
          <Text size="md" fw={600}>
            {isOnHold ? 'Remove the hold on this listing?' : 'Place this listing on hold?'}
          </Text>
        </Flex>
      </Flex>
      <Divider color="var(--colors-gray-0)" />
      <Flex gap="sm" w="100%" py="xl" px="md" mb="var(--safe-area-inset-bottom, 0)">
        <BNButton fullWidth variant="default" onClick={onClose}>
          Cancel
        </BNButton>
        <BNButton
          fullWidth
          variant="filled"
          loading={isLoading}
          color="green"
          disabled={currentUser?.roleId === 'ReadOnlyUser'}
          onClick={async () => {
            if (isOnHold) {
              await unreserveListing({ listingId: listing.listingId });
            } else {
              await reserveListing({
                listingId: listing.listingId,
              });
            }
            onClose();
          }}
          data-pendo-name={isOnHold ? 'Unreserve Button' : 'Reserve Button'}
        >
          Apply
        </BNButton>
      </Flex>
    </>
  );
}
